<template>
  <div class="bar-graph">
    <p v-if="errorMessage"
       :class="['text-center', {['error-label']: !graphData}]">
      {{errorMessage}}
    </p>
    <div class="bar-graph-wrapper">
      <tool-tip v-show="showInfo"
                :coordinates="toolTipCoordinates"
                :textLines="toolTipContent"
                yIsBottom></tool-tip>

      <svg v-if="hasData"
           class="bar-graph-svg"
           :viewbox="`0 0 ${height} ${width}`"
           :height="height"
           :width="width">

        <g v-if="yTicks" class="scale-axis y-axis">
          <g v-for="tick in yTicks"
             :key="tick.value"
             :transform="`translate(0, ${tick.yScaleTick})`">
            <text dominant-baseline="middle">{{tick.valueFormatted}}</text>
          </g>
        </g>

        <g v-if="yScale && xScale"
           class="bar-graph-paths"
           :transform="`translate(${margin}, ${hideXticks ? margin : 0})`">
          <g v-for="(bar, index) in graphData"
             :class="['graph-bar', {['is-highlighted']: activeBar === index}]"
             :key="index"
             @mouseover="highlightBar(bar, index)"
             @mouseout="hideHighlightBar">
            <!--this rect is so that the hover can happen on entire y axis -->
            <rect :height="height"
                  :width="pathWidth * barSelectorData.length"
                  :x="xScale(bar[xSelector])"></rect>

            <path-bar v-for="(selector, i) in barSelectorData"
                      :key="`${index}${i}`"
                      :centerTransform="calculateCenter(bar, 'amount')"
                      :height="calculatePathHeight(bar[selector])"
                      :pathClasses="`bar bar-${i}`"
                      :radius="radius"
                      :width="pathWidth"
                      :x="i % 2 ? xScale(bar[xSelector]) + pathWidth : xScale(bar[xSelector])"
                      :y="bar[selector] > 0 ? yScale(bar[selector]) : yScale(0)"
                      :topLeft="topRounded || !(i % 2)"
                      :topRight="topRounded || !!(i % 2)"></path-bar>
          </g>
        </g>

        <g v-if="xTicks"
           class="scale-axis x-axis"
           :transform="`translate(${marginDoubled}, ${height})`">
          <g v-for="tick in xTicks"
             :key="tick.value"
             :transform="`translate(${tick.xScaleTick}, 0)`">
            <text>
              <tspan v-for="word in tick.words" :x="`${width > 550 ? 0.5 : 0}em`" dy="1em" text-anchor="middle">{{word}}</tspan>
            </text>
          </g>
        </g>
      </svg>

    </div>
  </div>
</template>


<script>
  import * as d3 from 'd3'
  import { NumberFormatters } from '../mixins/NumberFormatters'
  import PathBar from './PathBar'
  import ToolTip from './ToolTip'

  export default {
    props: {
      barSelectorData: Array,
      centerBars: Boolean,
      graphData: Array,
      hideXticks: Boolean,
      margin: {
        default: 20,
        type: [Number, String]
      },
      numOfTicks: {
        default: 3,
        type: [Number, String]
      },
      radius: {
        default: 8,
        type: [Number, String]
      },
      toolTipContent: [Array, String],
      topRounded: Boolean,
      tSpanHeight: {
        default: 1,
        type: [Number, String]
      },
      useCurrency: Boolean,
      wrapperHeight: [Number, String],
      wrapperWidth: [Number, String],
      xPadding: {
        default: 0.3,
        type: [Number, String]
      },
      xScaleDomain: Array,
      xSelector: [Array, String],
      yTickFormat: String,
    },

    name: 'BarGraph',

    mixins: [NumberFormatters],

    components: {
      PathBar,
      ToolTip
    },

    data() {
      return {
        activeBar: null,
        errorMessage: null,
        height: null,
        showInfo: false,
        toolTipCoordinates: null,
        width: null,
        xScale: null,
        xTicks: null,
        yScale: null,
        yTicks: null,
      }
    },

    methods: {
      buildBottomAxis() {
        const view = this
        this.xScale = d3.scaleBand()
          .rangeRound([0, this.width])
          .domain(this.graphData.map((item) => item[this.xSelector]))
          .padding(this.xPadding)

        if (!this.hideXticks) {
          this.xTicks = this.xScale.domain().map(t => {
            return {
              value: t,
              words: t.split(' '),
              xScaleTick: view.xScale(t)
            }
          })
        }
      },

      buildLeftAxis() {
        const yMax = this.yScaleMax(),
          // have min yScale be -3% max, so zero will show some height
          // have max yScale be +3% max, so top label has enough height to not be clipped
          yDomain = yMax > 0 ? [-(yMax * .03), (yMax * 1.03)] : [-0.25, 3]

        this.yScale = d3.scaleLinear()
          .range([this.height, this.margin])
          .domain(yDomain)

        this.buildYTicks(yMax)
      },

      buildYTicks(yMax) {
        if (!this.hideXticks) {
          let ticks = []
          const range = yMax > 0 ? Math.ceil(yMax) : 3,
            third = range / 3,
            tickValues = [0, third, third * 2, range],
            view = this
          tickValues.forEach((t) => {
            let formattedT = this.shortenNumber(t, this.useCurrency)
            ticks.push({
              value: t,
              valueFormatted: formattedT,
              yScaleTick: view.yScale(t)
            })
          })
          this.yTicks = ticks
        }
      },

      calculateCenter(bar, selector) {
        const height = this.height,
          y = this.yScale(bar[selector])
        return this.centerBars ? `translate(0, ${((height - y) - height) / 2})` : null
      },

      calculatePathHeight(barValue) {
        const yScaleHeight = barValue > 0 ? this.yScale(barValue) : this.yScale(0)
        return this.height - yScaleHeight
      },

      configureGraph() {
        if (this.hasData) {
          this.errorMessage = null
          this.drawAxis()
        } else {
          this.errorMessage = 'data not available'
        }
      },

      drawAxis() {
        this.buildBottomAxis()
        this.buildLeftAxis()
      },

      getHighestItemKey(barItem) {
        let higherItem = 0,
          higherKey
        for (let key in barItem) {
          if (key !== this.xSelector && barItem[key] > higherItem) {
            higherItem = barItem[key]
            higherKey = key
          }
        }
        return higherKey
      },

      hideHighlightBar() {
        this.activeBar = null
        this.showInfo = false
      },

      highlightBar(barItem, index) {
        this.setToolTipCoords(barItem)
        this.activeBar = index
        this.showInfo = true
      },

      setGraphDimensions() {
        this.width = this.wrapperWidth - this.marginDoubled
        this.height = this.wrapperHeight ? this.wrapperHeight : (this.wrapperWidth * (3/4))
      },

      setToolTipCoords(barItem) {
        this.$emit('setToolTipContent', barItem)
        const highestKey = this.getHighestItemKey(barItem),
          highestKeyIndex = this.barSelectorData.indexOf(highestKey)

        let addX = (this.pathWidth * (highestKeyIndex + 1)),
          y = this.yScale(barItem[highestKey]),
          x = this.xScale(barItem[this.xSelector]),
          yNeg = ((this.height - y) - this.height)

        addX = barItem[highestKey] > 0 ? addX : addX + (this.pathWidth / 2)
        y = y ? y : this.height - this.margin  // in case all values are 0 or null, push it down
        y = this.centerBars ? y + yNeg : y
        this.toolTipCoordinates = {
          pushDirection: 'right',
          x: x + addX,
          y: y
        }
      },

      yScaleMax() {
        const view = this
        let allValues = []
        this.barSelectorData.forEach((selector) => view.graphData.map((item) => allValues.push(item[selector])))
        return d3.max(allValues)
      },
    },

    created() {
      this.setGraphDimensions()
    },

    mounted() {
      this.configureGraph()
    },

    computed: {
      hasData: function() {
        return this.graphData && this.graphData.length
      },

      marginDoubled: function() {
        return this.margin * 2
      },

      pathWidth: function() {
        return this.xScale.bandwidth() / this.barSelectorData.length
      },
    },

    watch: {
      graphData: function() {
        this.configureGraph()
      },

      wrapperWidth: function() {
        this.setGraphDimensions()
        if (this.hasData) { this.drawAxis() }
      },
    }
  }
</script>

<style lang="sass">
  //
  //  bar graph
  //
  .bar-graph-wrapper
    position: relative

    .bar-0
      fill: #128ff9

    .bar-1
      fill: #44e5e7

    .bar-graph-svg
      height: 100%
      width: 100%

      .tick
        line
          display: none

        text
          fill: #4a4a4a
          font-size: 1.2em

      .scale-axis
        font-size: 0.9em

        .domain
          display: none

      .graph-bar
        &.is-highlighted
          opacity: 0.5

        rect
          fill: transparent
</style>